import React, { CSSProperties, memo, useCallback } from 'react';
import { default as MuiCheckbox, CheckboxProps as MuiCheckboxProps } from '@material-ui/core/Checkbox';
import { SwitchBaseProps } from '@material-ui/core/internal/SwitchBase';
import { mergeClassNames } from '../../utils/cssClassNameHelper';
import { reactNodeToString } from '../../utils/reactNode';
import { FormControlLabel, FormControlLabelProps } from '../Form/FormControlLabel';
import { UdsFC } from '../../types';
import { tokens } from '../../theme';
import { Typography } from '../Typography';
import { Asterisk } from '../Asterisk/Asterisk';
import styled from 'styled-components';

export interface CheckboxProps {
  checked?: boolean;
  onChange?: (e: React.ChangeEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  id?: string;
  dashStyle?: boolean;
  required?: boolean;
  disabled?: boolean;
  subLabel?: React.ReactNode;
  className?: string;
  children?: any;
  style?: CSSProperties;
  dense?: boolean;
  error?: boolean;
  labelPlacement?: FormControlLabelProps['labelPlacement'];
  tabIndex?: number;
  name?: string;
  muiCheckboxProps?: MuiCheckboxProps;
}

export const Checkbox: UdsFC<CheckboxProps> = memo(
  ({ onChange, required, subLabel, muiCheckboxProps, error, ...props }) => {
    const dense = props.dense ?? true;
    const className = mergeClassNames(props.className, {
      disabled: props.disabled,
      checked: !!props.checked,
      dense
    });
    const isChecked = props.checked || props.dashStyle;

    const handleChange: SwitchBaseProps['onChange'] = useCallback(
      (event: React.ChangeEvent) => {
        onChange?.(event);
      },
      [onChange]
    );
    return (
      <FormControlLabel
        dataTest={props.dataTest}
        id={props.id}
        className={className}
        style={props.style ?? { padding: 0 }}
        labelPlacement={props.labelPlacement}
        onClick={props.onClick}
        onFocus={props.onFocus}
        control={
          <MuiCheckbox
            indeterminate={props.dashStyle ?? false}
            checked={isChecked ?? false}
            data-test="checkbox"
            inputProps={{
              'aria-label': props.children ? reactNodeToString(props.children) : 'checkbox',
              role: 'checkbox',
              style: { padding: 0 }
            }}
            id={props.id ? `${props.id}-input` : undefined}
            disabled={props.disabled ?? false}
            onChange={handleChange}
            aria-checked={
              typeof props.checked === 'undefined' ? undefined : (props.checked.toString() as 'true' | 'false')
            }
            color="primary"
            tabIndex={props.tabIndex}
            name={props.name}
            required={required}
            {...muiCheckboxProps}
          />
        }
        label={
          <>
            <StyledLabel
              className={`label ${props.checked ? 'checked' : 'unChecked'}`}
              $error={!!error}
              data-test="checkbox-label"
            >
              {props.children}
              {required ? (
                <>
                  {' '}
                  <Asterisk error={error} />
                </>
              ) : null}
            </StyledLabel>
            {!!subLabel && (
              <Typography style={{ color: tokens.colorOnSurfaceVariant, display: 'block' }}>{subLabel}</Typography>
            )}
          </>
        }
      />
    );
  }
);

export default Checkbox;

const StyledLabel = styled.span<{ $error: boolean }>`
    ${({ $error }) => ($error ? `& * { color: #f44336 }` : ``)},
    &:has(div):has(.MuiFormLabel-asterisk) {
      & > div {
        display: inline-block;
      }
    }
`;
